import { cn, type GroupImperativeHandle, Panel, PanelGroup, PanelHandle } from "@qw/ui";
import { useCallback, useEffect, useRef, useState } from "react";
import { Outlet } from "react-router";
import { Book } from "@/book/book";
import { INBOX_OUTLET_HOST, useInboxRail } from "@/inbox/use-inbox-rail";
import { useBook } from "@/providers/use-book";
import { FolderNav, type FolderNavVariant } from "./folder-nav";

const LEFT_SIDEBAR_SECTIONS_AUTOSAVE_ID = "left-sidebar-book-sections";
const FOLDERS_SECTION_MIN_SIZE_REM = 3;
const FOLDERS_SECTION_MIN_SIZE = `${FOLDERS_SECTION_MIN_SIZE_REM}rem`;
const FOLDERS_DROPDOWN_THRESHOLD_REM = 10;
const FOLDERS_SNAP_TOLERANCE_PX = 1;
const PANEL_LAYOUT_MATCH_TOLERANCE = 0.001;
const BOOK_SECTION_MIN_SIZE = "16rem";
const BOOK_SECTION_COLLAPSED_SIZE = "3rem";

type LeftSidebarSectionMode = "book" | "bookCollapsed" | "insured";

type LeftSidebarSectionSpec = {
  defaultShares: { folders: number; lower: number };
  /**
   * The variant a pinned mode knows up front. Declared rather than measured so a
   * swap never flashes the tall list nav for the frame before the resize
   * observer reports; the stored split has no fixed answer and measures for it.
   */
  folderNavVariant?: FolderNavVariant;
  folders: { defaultSize: string; maxSize?: string; minSize: string };
  /** A pinned split fixes both heights, so the handle and the drag snap are inert. */
  isSplitPinned: boolean;
  lower: { className?: string; defaultSize: string; id: string; maxSize?: string; minSize: string };
};

type LeftSidebarSectionLayout = Omit<LeftSidebarSectionSpec, "defaultShares"> & {
  defaultLayout: Record<string, number>;
  panelIds: string[];
};

/**
 * Derives a mode's group-level props from its two sections, so each mode names
 * its lower panel id once and seeds `defaultLayout` in the order the panels
 * render — `react-resizable-panels` matches those keys to the panels
 * positionally. Called at module scope so the results keep a stable identity:
 * `PanelGroup` re-applies the persisted layout whenever `panelIds` changes, so
 * rebuilding these per render would fight the broker's drag.
 */
const toSectionLayout = ({ defaultShares, ...section }: LeftSidebarSectionSpec): LeftSidebarSectionLayout => ({
  ...section,
  defaultLayout: { folders: defaultShares.folders, [section.lower.id]: defaultShares.lower },
  panelIds: ["folders", section.lower.id],
});

/**
 * The three vertical splits this sidebar can be in. `react-resizable-panels`
 * persists an autosaved group under a key built from its panel ids, so giving
 * each mode its own id set is what keeps their heights apart: the broker's
 * dragged folders/book split lives under a different key than either pinned
 * mode and comes back untouched from a swap.
 */
const LEFT_SIDEBAR_SECTION_LAYOUTS: Record<LeftSidebarSectionMode, LeftSidebarSectionLayout> = {
  // The stored split, and the only mode whose handle moves.
  book: toSectionLayout({
    defaultShares: { folders: 36, lower: 64 },
    folders: { defaultSize: "36%", minSize: FOLDERS_SECTION_MIN_SIZE },
    isSplitPinned: false,
    lower: { defaultSize: "64%", id: "book", minSize: BOOK_SECTION_MIN_SIZE },
  }),
  // Book view collapsed to its header row: the folders take the column.
  bookCollapsed: toSectionLayout({
    defaultShares: { folders: 100, lower: 0 },
    folderNavVariant: "list",
    folders: { defaultSize: "100%", minSize: "0%" },
    isSplitPinned: true,
    lower: {
      className: "border-t border-foreground/7.5",
      defaultSize: BOOK_SECTION_COLLAPSED_SIZE,
      id: "book-collapsed",
      maxSize: BOOK_SECTION_COLLAPSED_SIZE,
      minSize: BOOK_SECTION_COLLAPSED_SIZE,
    },
  }),
  // The mirror image: the insured takes the column and the folder nav yields to
  // the compact form the stored split already snaps to below 10rem.
  insured: toSectionLayout({
    defaultShares: { folders: 0, lower: 100 },
    folderNavVariant: "dropdown",
    folders: {
      defaultSize: FOLDERS_SECTION_MIN_SIZE,
      maxSize: FOLDERS_SECTION_MIN_SIZE,
      minSize: FOLDERS_SECTION_MIN_SIZE,
    },
    isSplitPinned: true,
    lower: { defaultSize: "100%", id: "insured", minSize: "0%" },
  }),
};

const getRemSizeInPixels = () => {
  if (typeof window === "undefined") {
    return 16;
  }

  const remSize = Number.parseFloat(window.getComputedStyle(document.documentElement).fontSize);

  return Number.isFinite(remSize) && remSize > 0 ? remSize : 16;
};

const arePanelLayoutsEqual = (firstLayout: Record<string, number>, secondLayout: Record<string, number>) => {
  const panelIds = new Set([...Object.keys(firstLayout), ...Object.keys(secondLayout)]);

  return [...panelIds].every((panelId) => {
    const firstPanelSize = firstLayout[panelId];
    const secondPanelSize = secondLayout[panelId];

    return (
      typeof firstPanelSize === "number" &&
      typeof secondPanelSize === "number" &&
      Math.abs(firstPanelSize - secondPanelSize) <= PANEL_LAYOUT_MATCH_TOLERANCE
    );
  });
};

export const LeftSidebar = ({ rootPath, withBorder = true }: { rootPath?: string; withBorder?: boolean }) => {
  const { isBookOpen, isBookViewExpanded } = useBook();
  const { outletHost } = useInboxRail();
  const showInsuredInRail = outletHost === INBOX_OUTLET_HOST.rail;
  // The insured is the taller of the two things sharing the lower section, so it
  // takes the column and the folder nav yields to its compact form. The
  // book-view collapse is a control over the book, so it stops applying while
  // the insured has the slot — otherwise the mini-app inherits the book's 3rem
  // collapsed height and the rail shows a sliver.
  const sectionMode: LeftSidebarSectionMode = showInsuredInRail
    ? "insured"
    : isBookViewExpanded
      ? "book"
      : "bookCollapsed";
  const sectionLayout = LEFT_SIDEBAR_SECTION_LAYOUTS[sectionMode];
  const { isSplitPinned } = sectionLayout;
  const panelGroupRef = useRef<GroupImperativeHandle | null>(null);
  const foldersPanelRef = useRef<HTMLDivElement | null>(null);
  const pendingSnappedLayoutRef = useRef<Record<string, number> | null>(null);
  const [foldersPanelHeight, setFoldersPanelHeight] = useState<number | null>(null);

  const updateFoldersPanelHeight = useCallback((height?: number) => {
    const measuredHeight = height ?? foldersPanelRef.current?.getBoundingClientRect().height ?? 0;
    const nextHeight = measuredHeight > 0 ? measuredHeight : null;

    setFoldersPanelHeight((currentHeight) => (currentHeight === nextHeight ? currentHeight : nextHeight));
  }, []);

  const handleFoldersPanelLayoutChanged = useCallback(
    (layout: Record<string, number>) => {
      if (isSplitPinned) {
        return;
      }

      if (pendingSnappedLayoutRef.current) {
        if (arePanelLayoutsEqual(layout, pendingSnappedLayoutRef.current)) {
          pendingSnappedLayoutRef.current = null;
          return;
        }

        pendingSnappedLayoutRef.current = null;
      }

      const foldersPercent = layout.folders;
      const bookPercent = layout.book;
      const measuredPanelHeight = foldersPanelRef.current?.getBoundingClientRect().height ?? 0;
      const measuredHeight = measuredPanelHeight > 0 ? measuredPanelHeight : (foldersPanelHeight ?? 0);

      if (measuredPanelHeight > 0) {
        updateFoldersPanelHeight(measuredPanelHeight);
      }

      if (
        typeof foldersPercent !== "number" ||
        typeof bookPercent !== "number" ||
        !Number.isFinite(foldersPercent) ||
        !Number.isFinite(bookPercent) ||
        foldersPercent <= 0 ||
        measuredHeight <= 0
      ) {
        return;
      }

      const remSize = getRemSizeInPixels();
      const minHeight = FOLDERS_SECTION_MIN_SIZE_REM * remSize;
      const dropdownThreshold = FOLDERS_DROPDOWN_THRESHOLD_REM * remSize;

      if (measuredHeight <= minHeight + FOLDERS_SNAP_TOLERANCE_PX || measuredHeight > dropdownThreshold) {
        return;
      }

      const groupHeight = measuredHeight / (foldersPercent / 100);
      const snappedFoldersPercent = Number.parseFloat(((minHeight / groupHeight) * 100).toFixed(3));
      const snappedBookPercent = Number.parseFloat((100 - snappedFoldersPercent).toFixed(3));

      if (!Number.isFinite(groupHeight) || !Number.isFinite(snappedFoldersPercent)) {
        return;
      }

      const snappedLayout = {
        ...layout,
        folders: snappedFoldersPercent,
        book: Math.max(0, snappedBookPercent),
      };
      const panelGroup = panelGroupRef.current;

      if (!panelGroup) {
        return;
      }

      pendingSnappedLayoutRef.current = snappedLayout;
      panelGroup.setLayout(snappedLayout);
      updateFoldersPanelHeight(minHeight);
    },
    [foldersPanelHeight, isSplitPinned, updateFoldersPanelHeight]
  );

  useEffect(() => {
    if (isSplitPinned) {
      setFoldersPanelHeight(null);
      return;
    }

    const foldersPanel = foldersPanelRef.current;

    if (!foldersPanel) {
      return;
    }

    updateFoldersPanelHeight();

    if (typeof ResizeObserver === "undefined") {
      const handleWindowResize = () => updateFoldersPanelHeight();

      window.addEventListener("resize", handleWindowResize);
      return () => {
        window.removeEventListener("resize", handleWindowResize);
      };
    }

    const resizeObserver = new ResizeObserver(([entry]) => {
      updateFoldersPanelHeight(entry?.contentRect.height);
    });

    resizeObserver.observe(foldersPanel);

    return () => {
      resizeObserver.disconnect();
    };
  }, [isSplitPinned, updateFoldersPanelHeight]);

  if (!isBookOpen) {
    return null;
  }

  const folderNavVariant =
    sectionLayout.folderNavVariant ??
    (foldersPanelHeight !== null && foldersPanelHeight <= FOLDERS_DROPDOWN_THRESHOLD_REM * getRemSizeInPixels()
      ? "dropdown"
      : "list");

  return (
    <aside className={cn("flex h-full min-w-0 flex-1 flex-col bg-background", withBorder && "border-r")}>
      <PanelGroup
        direction="vertical"
        autoSaveId={LEFT_SIDEBAR_SECTIONS_AUTOSAVE_ID}
        defaultLayout={sectionLayout.defaultLayout}
        groupRef={panelGroupRef}
        onLayoutChanged={handleFoldersPanelLayoutChanged}
        panelIds={sectionLayout.panelIds}
        className="min-h-0"
      >
        <Panel
          id="folders"
          minSize={sectionLayout.folders.minSize}
          maxSize={sectionLayout.folders.maxSize}
          defaultSize={sectionLayout.folders.defaultSize}
          className="overflow-hidden"
        >
          <div ref={foldersPanelRef} className="h-full min-h-0">
            <FolderNav className="h-full" variant={folderNavVariant} />
          </div>
        </Panel>
        <PanelHandle hidden={isSplitPinned} disabled={isSplitPinned} />
        <Panel
          id={sectionLayout.lower.id}
          minSize={sectionLayout.lower.minSize}
          maxSize={sectionLayout.lower.maxSize}
          defaultSize={sectionLayout.lower.defaultSize}
          className={cn("overflow-hidden", sectionLayout.lower.className)}
        >
          {showInsuredInRail ? <Outlet /> : <Book rootPath={rootPath} withBorder={false} />}
        </Panel>
      </PanelGroup>
    </aside>
  );
};
